import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { AlertCircle, Loader2 } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"
import { z } from "zod"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import {
  createFormGeneratorFactory,
  FieldType,
  type SelectOption,
} from "@/components/form-generator"
import { Page } from "@/components/page"
import { RecommendationsSection } from "@/components/recommendations-section"
import { RefineReportsSection } from "@/components/refine-reports-section"
import { ExportToPowerPoint } from "@/components/reports/export-to-powerpoint"
import { ReportSlideRenderer } from "@/components/reports/report-slide-renderer"
import { EstateSummary } from "@/components/reports/report-slides/EstateSummary"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Typography } from "@/components/ui/typography"
import {
  ReportTypes,
  STANDARD_REPORT_TYPES,
} from "../../../../../../../api/report-generation/report-types"
import type { ReportTypesFromSchema } from "../../../../../../../api/util/prompts"

const REPORT_RENDER_ORDER: ReportTypesFromSchema[] = [
  ReportTypes.ESTATE_SUMMARY,
  ReportTypes.ESTATE_GOALS_AND_RELATIONSHIPS,
  ReportTypes.ASSET_OWNERSHIP_AND_BENEFICIARIES,
  ReportTypes.HEALTH,
  ReportTypes.TAXES,
  ReportTypes.PROBATE,
  ReportTypes.INSURANCE,
  ReportTypes.TRUST,
  ReportTypes.CORPORATE,
]

export const Route = createFileRoute("/_authed/_professional/professional/reports/$estateId")({
  component: RouteComponent,
})

function formatReportType(reportType: ReportTypesFromSchema, t: (key: string) => string): string {
  return t(`pages.reports.estate.types.${reportType}`) || reportType
}

function RouteComponent() {
  const { estateId } = Route.useParams()
  const { t } = useTranslation()
  const [isGeneratingReports, setIsGeneratingReports] = useState(false)

  const formFactory = useMemo(() => createFormGeneratorFactory(), [])

  const {
    data: estate,
    isLoading: estateLoading,
    error: estateError,
  } = trpc.estates.getById.useQuery({ estateId })

  const {
    data: reports = [],
    isLoading: reportsLoading,
    error: reportsError,
  } = trpc.reports.list.useQuery({ estateId, includeContent: true })

  const generateReportsMutation = trpc.reports.generate.useMutation({
    onMutate: () => {
      setIsGeneratingReports(true)
    },
    onSuccess: () => {
      toast.success(t("pages.reports.estate.generateReports.success"), {
        description: t("pages.reports.estate.generateReports.emailNotice"),
        duration: 8000,
      })
    },
    onError: (error) => {
      setIsGeneratingReports(false)
      toast.error(error.message)
    },
  })

  const regenerateReportMutation = trpc.reports.regenerate.useMutation({
    onSuccess: () => {
      toast.success(t("pages.reports.refine.success"))
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const reportIdMap: readonly SelectOption[] = useMemo(() => {
    return STANDARD_REPORT_TYPES.map((reportType) => ({
      id: reports.find((report) => report.reportType === reportType)?.id || "",
      label: formatReportType(reportType, t),
    }))
  }, [t, reports])

  // Define form fields
  const refineFormFields = useMemo(
    () =>
      [
        {
          name: "reportType",
          label: "Report Type",
          type: FieldType.Select,
          required: true,
          default: "",
          schema: z.string().min(1, "Please select a report to refine"),
          placeholder: "Select a report type",
          options: reportIdMap,
          children: () => null,
        },
        {
          name: "additionalPrompt",
          label: "Refinement Instructions",
          type: FieldType.Textarea,
          required: true,
          default: "",
          schema: z.string().min(1, "Please provide instructions for refining the report"),
          placeholder:
            "Describe how you'd like to refine this report (e.g., 'Focus more on tax implications' or 'Include specific recommendations for charitable giving')",
        },
      ] as const,
    [reportIdMap],
  )

  // Create form component
  const RefineForm = useMemo(
    () => formFactory.createForm(refineFormFields),
    [formFactory, refineFormFields],
  )

  // Register submit handler
  RefineForm.getSubmitHandler(({ reportType, additionalPrompt }) => {
    regenerateReportMutation.mutate({
      reportId: reportType,
      additionalPrompt,
    })
  })

  const isLoading = estateLoading || reportsLoading
  const error = estateError || reportsError
  const individualReports =
    reports?.filter(
      (report) =>
        report.reportType !== ReportTypes.ESTATE_SUMMARY &&
        report.reportType !== ReportTypes.WILL_REVIEW &&
        report.reportType !== ReportTypes.INSURANCE_REVIEW &&
        report.reportType !== ReportTypes.TRUST_REVIEW,
    ) || []
  const orderIndex = useMemo(() => new Map(REPORT_RENDER_ORDER.map((t, i) => [t, i])), [])
  const orderedIndividualReports = useMemo(() => {
    return [...individualReports].sort((a, b) => {
      const ai = orderIndex.get(a.reportType as ReportTypesFromSchema) ?? 999
      const bi = orderIndex.get(b.reportType as ReportTypesFromSchema) ?? 999
      return ai - bi
    })
  }, [individualReports, orderIndex])

  if (isLoading) {
    return (
      <Page
        headerSlot={<ClientHeader estateId={estateId} />}
        tabSlot={<ClientTabNavigation estateId={estateId} />}
      >
        <div>{t("pages.reports.estate.loading.content")}</div>
      </Page>
    )
  }

  if (error || !estate) {
    return (
      <Page
        headerSlot={<ClientHeader estateId={estateId} />}
        tabSlot={<ClientTabNavigation estateId={estateId} />}
      >
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-medium text-red-800">{t("pages.reports.estate.error.message")}</p>
          <p className="text-red-700">
            {error?.message || t("pages.reports.estate.error.estateNotFound")}
          </p>
        </div>
      </Page>
    )
  }

  const summaryReport = reports?.find((report) => report.reportType === ReportTypes.ESTATE_SUMMARY)

  return (
    <Page
      headerSlot={<ClientHeader estateId={estateId} />}
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <div className="flex justify-end">
          <ExportToPowerPoint estateId={estateId} />
        </div>

        {individualReports.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                {t("pages.reports.estate.noReports.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Typography as="p" className="text-muted-foreground mb-4">
                {t("pages.reports.estate.noReports.message")}
              </Typography>
              <Button
                variant="outline"
                onClick={() =>
                  generateReportsMutation.mutate({
                    estateId,
                    reportType: [],
                  })
                }
                disabled={isGeneratingReports}
              >
                {isGeneratingReports ? (
                  <>
                    <Loader2 className="animate-spin" />{" "}
                    {t("pages.reports.estate.generateReports.buttonGenerating")}
                  </>
                ) : (
                  t("pages.reports.estate.generateReports.button")
                )}
              </Button>
              <Typography as="p" className="text-xs text-muted-foreground mt-3">
                * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                insight for each client, saving hours of work. Due to the complex nature of the
                report generation process you can expect each report to take between 10-20 minutes
                as our agents evaluate the latest estate laws, user goals, and user data to deliver
                a truly bespoke output for your clients and their families.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <RecommendationsSection
            title="Personalized recommendation"
            refineSection={
              <RefineReportsSection>
                <RefineForm
                  submitLabel="Refine Report"
                  isSubmitting={regenerateReportMutation.isPending}
                />
                <Typography as="p" className="text-xs text-muted-foreground mt-3">
                  * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                  insight for each client, saving hours of work. Due to the complex nature of the
                  report generation process you can expect each report to take between 10-20 minutes
                  as our agents evaluate the latest estate laws, user goals, and user data to
                  deliver a truly bespoke output for your clients and their families.
                </Typography>
              </RefineReportsSection>
            }
          >
            <EstateSummary estateId={estateId} summaryReport={summaryReport} />
            {/* Individual Reports */}
            {individualReports.length > 0 && (
              <div className="space-y-6">
                {orderedIndividualReports.map((report) => (
                  <Card key={report.id}>
                    <CardContent>
                      <CardTitle className="text-[#2B6CB0] text-lg font-semibold mb-3">
                        {formatReportType(report.reportType, t)}
                      </CardTitle>

                      <div className="mb-4">
                        <ReportSlideRenderer
                          estateId={estateId}
                          reportType={report.reportType}
                          estateName={estate.estateName}
                        />
                      </div>

                      {report.content ? (
                        <Collapsible className="group">
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="link"
                              className="p-0 h-auto text-teal-600 hover:text-teal-700"
                            >
                              <span className="group-data-[state=closed]:block group-data-[state=open]:hidden">
                                Read more
                              </span>
                              <span className="group-data-[state=closed]:hidden group-data-[state=open]:block">
                                Collapse
                              </span>
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4">
                            <div className="prose max-w-none prose-slate">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {report.content}
                              </ReactMarkdown>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : (
                        <Button
                          variant="link"
                          className="p-0 h-auto text-teal-600 hover:text-teal-700"
                        >
                          Read more
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </RecommendationsSection>
        )}
      </div>
    </Page>
  )
}
